// src/Pages/Blog/BlogListPage.jsx
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  useTheme,
  useMediaQuery,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import PlaceholderImage from './PlaceholderImage';
import { BLOG_POSTS } from './blogData';

const BlogListPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set(['all']);
    BLOG_POSTS.forEach(post => {
      post.categories.forEach(cat => cats.add(cat));
    });
    return Array.from(cats);
  }, []);

  // Filter posts
  const filteredPosts = useMemo(() => {
    return BLOG_POSTS.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           post.author.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || 
                             post.categories.includes(selectedCategory);

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * postsPerPage;
    return filteredPosts.slice(startIndex, startIndex + postsPerPage);
  }, [filteredPosts, currentPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Blog List Schema for SEO
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Revfer Blog",
    "description": "Expert insights on AI automation, small business growth, and local business marketing",
    "url": "https://www.revfer.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Revfer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.revfer.com/logo.png"
      }
    },
    "blogPost": BLOG_POSTS.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "image": post.image,
      "datePublished": post.publishedDate,
      "dateModified": post.modifiedDate,
      "author": {
        "@type": "Person",
        "name": post.author.name
      },
      "description": post.excerpt,
      "url": `https://www.revfer.com/blog/${post.slug}`
    }))
  };

  return (
    <>
      <EnhancedSEO 
        title="Revfer Blog - AI Automation & Small Business Growth Insights"
        description="Expert insights on AI automation, customer service optimization, local business marketing, and growth strategies for small businesses. Learn how to leverage AI to scale your business."
        keywords="AI automation blog, small business tips, customer service automation, business growth strategies, AI for small business, local business marketing, business efficiency, entrepreneurship blog, AI insights, business automation"
        type="website"
        image="/images/blog-og.jpg"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' }
        ]}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(blogListSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
              textAlign: 'center'
            }}
          >
            Revfer Blog
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1rem', md: '1.3rem' },
              textAlign: 'center',
              opacity: 0.95,
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            Expert insights on AI automation, business growth, and building trust
            in local communities
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        {/* Search and Filter Section */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ bgcolor: 'white' }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <CategoryIcon />
                    </InputAdornment>
                  }
                >
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Results Count */}
          <Typography
            variant="body2"
            sx={{ mt: 2, color: 'text.secondary' }}
          >
            {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>

        {/* Featured Post (First Post) */}
        {currentPage === 1 && paginatedPosts.length > 0 && (
          <Card
            sx={{
              mb: 6,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: 3,
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardActionArea onClick={() => handlePostClick(paginatedPosts[0].slug)}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  {paginatedPosts[0].image ? (
                    <CardMedia
                      component="img"
                      image={paginatedPosts[0].image}
                      alt={paginatedPosts[0].title}
                      sx={{
                        height: { xs: 250, md: 400 },
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <PlaceholderImage
                      title={paginatedPosts[0].title}
                      categories={paginatedPosts[0].categories}
                      height={400}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <CardContent sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<TrendingIcon />}
                        label="Featured"
                        color="primary"
                        size="small"
                      />
                      {paginatedPosts[0].categories.map(cat => (
                        <Chip
                          key={cat}
                          label={cat}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        fontWeight: 700,
                        mb: 2,
                        color: 'text.primary'
                      }}
                    >
                      {paginatedPosts[0].title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {paginatedPosts[0].excerpt}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        component="img"
                        src={paginatedPosts[0].author.avatar}
                        alt={paginatedPosts[0].author.name}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {paginatedPosts[0].author.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(paginatedPosts[0].publishedDate)} • {paginatedPosts[0].readTime} min read
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Grid>
              </Grid>
            </CardActionArea>
          </Card>
        )}

        {/* Blog Grid */}
        <Grid container spacing={3}>
          {paginatedPosts.slice(currentPage === 1 ? 1 : 0).map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 2,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handlePostClick(post.slug)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  {post.image ? (
                    <CardMedia
                      component="img"
                      image={post.image}
                      alt={post.title}
                      sx={{
                        height: 200,
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <PlaceholderImage
                      title={post.title}
                      categories={post.categories}
                      height={200}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {post.categories.slice(0, 2).map(cat => (
                        <Chip
                          key={cat}
                          label={cat}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '3em'
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {post.excerpt}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto' }}>
                      <Box
                        component="img"
                        src={post.author.avatar}
                        alt={post.author.name}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                          {post.author.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(post.publishedDate)} • {post.readTime} min
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* No Results */}
        {filteredPosts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
              No articles found
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? 'medium' : 'large'}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Container>
    </>
  );
};

export default BlogListPage;